import type { ReactNode } from "react";
import type { AttentionSeverity } from "@/types";
import {
  doctorToneKey,
  healthToneKey,
  mapDoctorLabel,
  mapHealthLabel,
  type DoctorLabel,
  type HealthLabel,
} from "@aisecure/status";

type BadgeVariant = "success" | "warning" | "danger" | "neutral" | "accent";

interface BadgeProps {
  variant?: BadgeVariant;
  children: ReactNode;
}

export function Badge({ variant = "neutral", children }: BadgeProps) {
  return <span className={`badge badge--${variant}`}>{children}</span>;
}

interface StatusDotProps {
  status: "green" | "yellow" | "red" | "unknown";
  label?: string;
}

export function StatusDot({ status, label }: StatusDotProps) {
  return (
    <span
      className={`status-dot status-dot--${status}`}
      role="img"
      aria-label={label ?? status}
    />
  );
}

const toneToBadge: Record<string, BadgeVariant> = {
  success: "success",
  warning: "warning",
  danger: "danger",
  neutral: "neutral",
};

export function healthToBadgeVariant(
  health: "healthy" | "degraded" | "critical" | HealthLabel,
): BadgeVariant {
  const label =
    health === "Healthy" ||
    health === "Degraded" ||
    health === "Critical" ||
    health === "Unknown"
      ? health
      : mapHealthLabel(health, true);
  return toneToBadge[healthToneKey(label)] ?? "neutral";
}

export function doctorToBadgeVariant(
  status: "pass" | "warn" | "fail" | "unknown" | "not_scanned" | DoctorLabel,
): BadgeVariant {
  const label =
    status === "Pass" ||
    status === "Warn" ||
    status === "Fail" ||
    status === "Not scanned" ||
    status === "Unknown"
      ? status
      : mapDoctorLabel(status);
  return toneToBadge[doctorToneKey(label)] ?? "neutral";
}

export function doctorLabel(
  status: "pass" | "warn" | "fail" | "unknown" | "not_scanned" | DoctorLabel,
): string {
  if (
    status === "Pass" ||
    status === "Warn" ||
    status === "Fail" ||
    status === "Not scanned" ||
    status === "Unknown"
  ) {
    return status;
  }
  return mapDoctorLabel(status);
}

export function healthLabel(
  health: "healthy" | "degraded" | "critical" | HealthLabel,
): HealthLabel {
  if (
    health === "Healthy" ||
    health === "Degraded" ||
    health === "Critical" ||
    health === "Unknown"
  ) {
    return health;
  }
  return mapHealthLabel(health, true);
}

export function severityToBadgeVariant(severity: AttentionSeverity): BadgeVariant {
  if (severity === "critical") return "danger";
  if (severity === "high") return "warning";
  if (severity === "medium") return "accent";
  return "neutral";
}

export function severityLabel(severity: AttentionSeverity): string {
  return severity.charAt(0).toUpperCase() + severity.slice(1);
}
